import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, isToday, isFuture, parseISO } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select'
import { Accordion, AccordionContent, AccordionItem } from '../ui/accordion'
import { DayGoalsList } from './DayGoalsList'
import { DayClimbsList } from './DayClimbsList'
import { GoalFormDialog } from './GoalFormDialog'
import { WorkoutList } from './WorkoutList'
import { AddWorkoutDialog } from './AddWorkoutDialog'
import { DaySelectorRow } from './DaySelectorRow'
import { cn } from '../../lib/utils'
import { SESSION_TYPE_CATEGORIES } from '../../types/training-session'
import type { TrainingSession, SessionType } from '../../types/training-session'
import type { Goal } from '../../types/goal'
import type { Workout } from '../../types/workout'
import type { WorkoutResultMatch } from '../../utils/match-workout-results'
import type { DayAttempts, AscentItem } from '../../services/training-diary'

export interface SessionPayload {
  session_date: string
  session_type: string
  effort: number | null
  duration_minutes: number | null
  note: string | null
}

const EFFORT_KEYS = [
  'trainingDiary.effort.veryEasy',
  'trainingDiary.effort.easy',
  'trainingDiary.effort.moderate',
  'trainingDiary.effort.hard',
  'trainingDiary.effort.veryHard',
]

// yellow - blue - green - orange - red
const EFFORT_SELECTED_CLASSES = [
  'bg-yellow-400 border-yellow-400 text-yellow-950',
  'bg-blue-500 border-blue-500 text-white',
  'bg-green-500 border-green-500 text-white',
  'bg-orange-400 border-orange-400 text-white',
  'bg-red-500 border-red-500 text-white',
]

const EFFORT_DOT_CLASSES = [
  'bg-yellow-400',
  'bg-blue-500',
  'bg-green-500',
  'bg-orange-400',
  'bg-red-500',
]

// Start at 45m, go up in 15m steps
const DURATION_OPTIONS = Array.from({ length: 22 }, (_, i) => (i + 3) * 15)

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function EffortDots({ effort }: { effort: number }) {
  return (
    <div className="flex gap-0.5 items-center shrink-0">
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={cn(
            'size-2 rounded-full',
            i < effort ? EFFORT_DOT_CLASSES[effort - 1] : 'bg-muted-foreground/25'
          )}
        />
      ))}
    </div>
  )
}

interface DayRowProps {
  date: string
  weekStart: string
  session: TrainingSession | null
  goals: Goal[]
  workouts: Workout[]
  workoutMatches: Map<string, WorkoutResultMatch>
  attempts: DayAttempts
  ascents: AscentItem[]
  scheduleMode: boolean
  onSessionCreate: (data: SessionPayload) => Promise<void>
  onSessionUpdate: (id: string, data: SessionPayload) => Promise<void>
  onSessionDelete: (id: string) => Promise<void>
  onGoalCreate: (data: { body: string; target_date: string }) => Promise<void>
  onGoalUpdate: (id: string, data: { body: string; target_date: string }) => Promise<void>
  onWorkoutCreate: (exerciseId: string) => Promise<void>
  onWorkoutDayPick: (workoutId: string, date: string | null) => void
  /** The day's workouts in their new running order. */
  onWorkoutReorder: (ids: string[]) => void
  /** Opens the add-workout dialog as soon as the row mounts (dashboard deep link). */
  autoOpenAddWorkout?: boolean
  /** Called once the dialog has been auto-opened, so it happens only the once. */
  onAutoOpenAddWorkoutHandled?: () => void
}

interface SessionFormProps {
  date: string
  isFuture: boolean
  session?: TrainingSession | null
  onSave: (data: SessionPayload) => Promise<void>
  onDelete?: () => Promise<void>
  onCancel: () => void
}

function SessionForm({ date, isFuture, session, onSave, onDelete, onCancel }: SessionFormProps) {
  const { t } = useTranslation()
  const [sessionType, setSessionType] = useState<SessionType>(
    (session?.session_type as SessionType) ?? 'free_indoor_boulder'
  )
  const [effort, setEffort] = useState<number>(session?.effort ?? 3)
  const [durationMinutes, setDurationMinutes] = useState<string>(
    session?.duration_minutes?.toString() ?? 'none'
  )
  const [note, setNote] = useState(session?.note ?? '')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await onSave({
        session_date: date,
        session_type: sessionType,
        effort: isFuture ? null : effort,
        duration_minutes: !isFuture && durationMinutes && durationMinutes !== 'none' ? parseInt(durationMinutes) : null,
        note: note.trim() || null,
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-1">
      {/* Session type */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('trainingDiary.dialog.session.type', 'Session type')}</Label>
        <Select value={sessionType} onValueChange={(v) => setSessionType(v as SessionType)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SESSION_TYPE_CATEGORIES.map(({ category, types }) => (
              <SelectGroup key={category}>
                <SelectLabel className="pl-2 text-xs uppercase tracking-wide text-muted-foreground">
                  {t(`trainingDiary.sessionCategory.${category}`)}
                </SelectLabel>
                {types.map((type) => (
                  <SelectItem key={type} value={type} className="font-normal">
                    {t(`trainingDiary.sessionType.${type}`)}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Effort */}
      {!isFuture && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('trainingDiary.effort.label', 'Effort')}</Label>
          <div className="flex gap-1">
            {EFFORT_KEYS.map((key, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setEffort(idx + 1)}
                className={cn(
                  'flex-1 py-1 rounded-md border transition-colors flex flex-col items-center',
                  effort === idx + 1
                    ? EFFORT_SELECTED_CLASSES[idx]
                    : 'bg-background text-foreground border-border hover:bg-accent'
                )}
              >
                <span className="text-xs font-medium">{idx + 1}</span>
                <span className="text-[10px] leading-tight text-center truncate w-full px-0.5">
                  {t(key)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Duration */}
      {!isFuture && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('trainingDiary.duration.label', 'Duration (optional)')}</Label>
          <Select value={durationMinutes} onValueChange={setDurationMinutes}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder={t('trainingDiary.duration.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('trainingDiary.duration.none', 'Not specified')}</SelectItem>
              {DURATION_OPTIONS.map((m) => (
                <SelectItem key={m} value={m.toString()}>
                  {formatDuration(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Note */}
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('trainingDiary.note', 'Note (optional)')}</Label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={2000}
          rows={2}
          className="text-sm resize-none"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-destructive hover:text-destructive px-2"
            onClick={handleDelete}
            disabled={deleting}
          >
            <Trash2 className="size-3 mr-1" />
            {t('common.delete', 'Delete')}
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" size="sm" className="h-8 text-xs" disabled={saving}>
            {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export function DayRow({
  date,
  weekStart,
  session,
  goals,
  workouts,
  workoutMatches,
  attempts,
  ascents,
  scheduleMode,
  onSessionCreate,
  onSessionUpdate,
  onSessionDelete,
  onGoalCreate,
  onGoalUpdate,
  onWorkoutCreate,
  onWorkoutDayPick,
  onWorkoutReorder,
  autoOpenAddWorkout = false,
  onAutoOpenAddWorkoutHandled,
}: DayRowProps) {
  const { t, i18n } = useTranslation()
  const [accordionValue, setAccordionValue] = useState<string>('')
  const [goalDialogOpen, setGoalDialogOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)
  const [addWorkoutOpen, setAddWorkoutOpen] = useState(false)

  useEffect(() => {
    if (!autoOpenAddWorkout) return
    setAddWorkoutOpen(true)
    onAutoOpenAddWorkoutHandled?.()
  }, [autoOpenAddWorkout, onAutoOpenAddWorkoutHandled])

  async function handleAddWorkouts(exerciseIds: string[]) {
    for (const id of exerciseIds) {
      await onWorkoutCreate(id)
    }
  }

  const dateObj = parseISO(date)
  const isFutureDay = isFuture(dateObj)
  const isTodayDay = isToday(dateObj)
  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS
  const dayLabel = format(dateObj, 'EEE d', { locale: dateFnsLocale })

  const sessionOpen = accordionValue === 'session'

  function toggleSession() {
    setAccordionValue((v) => v === 'session' ? '' : 'session')
  }

  function closeAccordion() {
    setAccordionValue('')
  }

  async function handleSessionSave(data: SessionPayload) {
    if (session) {
      await onSessionUpdate(session.id, data)
    } else {
      await onSessionCreate(data)
    }
    closeAccordion()
  }

  async function handleSessionDelete() {
    if (!session) return
    await onSessionDelete(session.id)
    closeAccordion()
  }

  function openEditGoal(g: Goal) {
    setEditingGoal(g)
    setGoalDialogOpen(true)
  }

  async function handleGoalSave(data: { body: string; target_date: string }) {
    if (editingGoal) {
      await onGoalUpdate(editingGoal.id, data)
    } else {
      await onGoalCreate(data)
    }
  }

  return (
    <div
      id={`day-row-${date}`}
      data-date={date}
      className={cn(
        'rounded-xl border p-3 flex flex-col gap-2 scroll-mt-32',
        isTodayDay && 'border-primary/50 bg-primary/5'
      )}
    >
      {/* Header row: date label | session button | + workout | sleep */}
      <div className="flex items-center gap-2">
        <span className={cn(
          'text-sm font-medium shrink-0',
          isTodayDay && 'text-primary',
          isFutureDay && 'text-muted-foreground'
        )}>
          {dayLabel}
          {isTodayDay && (
            <span className="ml-1.5 text-xs text-primary">{t('trainingDiary.today', 'Today')}</span>
          )}
        </span>

        <div className="flex items-center gap-1 ml-auto">
          {session ? (
            /* Session summary button — toggles accordion */
            <button
              type="button"
              onClick={toggleSession}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors',
                'hover:bg-accent text-left max-w-[220px]',
                sessionOpen && 'bg-accent'
              )}
            >
              <span className="text-xs font-medium text-foreground/80 truncate">
                {session.session_type_label}
              </span>
              {session.effort != null && <EffortDots effort={session.effort} />}
              {session.duration_minutes && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDuration(session.duration_minutes)}
                </span>
              )}
            </button>
          ) : (
            /* Add session button (compact) */
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-dashed px-2"
              onClick={toggleSession}
            >
              <Plus className="size-3 mr-0.5" />
              {t('trainingDiary.empty.sessionShort', 'session')}
            </Button>
          )}

          {/* Add workout button (compact) */}
          {!scheduleMode && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-dashed px-2"
              onClick={() => setAddWorkoutOpen(true)}
            >
              <Plus className="size-3 mr-0.5" />
              {t('trainingDiary.workouts.addButtonShort', 'workout')}
            </Button>
          )}
        </div>
      </div>

      {/* Note preview — visible only when accordion is closed */}
      {session?.note && !sessionOpen && (
        <p className="text-xs text-muted-foreground truncate -mt-1">{session.note}</p>
      )}

      {/* Session form — animated accordion */}
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="session" className="border-0">
          <AccordionContent className="pb-0 pt-0">
            <SessionForm
              date={date}
              isFuture={isFutureDay}
              session={session}
              onSave={handleSessionSave}
              onDelete={session ? handleSessionDelete : undefined}
              onCancel={closeAccordion}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Workouts of the day, in the order they are meant to be done */}
      {workouts.length > 0 && (
        <WorkoutList
          workouts={workouts}
          workoutMatches={workoutMatches}
          hideResults={scheduleMode}
          sortable={!scheduleMode}
          onReorder={onWorkoutReorder}
          renderFooter={(w) =>
            scheduleMode ? (
              <DaySelectorRow
                weekStart={weekStart}
                selectedDate={w.scheduled_date}
                onSelect={(d) => onWorkoutDayPick(w.id, d)}
              />
            ) : null
          }
        />
      )}

      {/* Climbs of the day, grouped by location: ascents first, then attempts */}
      <DayClimbsList ascents={ascents} attempts={attempts.items} />

      {/* Goals */}
      <DayGoalsList goals={goals} onEdit={openEditGoal} />

      {/* Goal dialog */}
      <GoalFormDialog
        open={goalDialogOpen}
        onOpenChange={setGoalDialogOpen}
        initialDate={date}
        goal={editingGoal}
        onSave={handleGoalSave}
      />

      {/* Add workout dialog */}
      <AddWorkoutDialog
        open={addWorkoutOpen}
        onOpenChange={setAddWorkoutOpen}
        onConfirm={handleAddWorkouts}
      />
    </div>
  )
}
